/**
 * Serviço de Frequência
 *
 * Gerencia operações de frequência usando RPC functions para lógica complexa.
 * Seguindo a arquitetura de microserviços, operações com validações e lógica vão para RPC.
 */

// Usar importação dinâmica apenas quando necessário para evitar dependência circular
let postgrestClientModule = null;
const getPostgrestClient = async () => {
  if (!postgrestClientModule) {
    postgrestClientModule = await import("../lib/postgrest/client");
  }
  return postgrestClientModule.postgrestClient;
};

/**
 * Serviço de frequência
 */
export const frequenciaService = {
  /**
   * Registra frequência de um aluno (RPC function)
   * @param {Object} frequenciaData
   * @param {string} frequenciaData.alunoId
   * @param {string} frequenciaData.turmaId
   * @param {string} frequenciaData.data - Data no formato YYYY-MM-DD
   * @param {boolean} frequenciaData.presente
   * @param {string} frequenciaData.observacoes
   * @param {string} [frequenciaData.atividadeId] - ID da atividade (opcional)
   * @returns {Promise<Object>}
   */
  async registrar(frequenciaData) {
    const client = await getPostgrestClient();
    return client.rpc("registrar_frequencia", {
      p_aluno_id: frequenciaData.alunoId,
      p_turma_id: frequenciaData.turmaId,
      p_data: frequenciaData.data,
      p_presente: frequenciaData.presente,
      p_observacoes: frequenciaData.observacoes || null,
      p_atividade_id: frequenciaData.atividadeId || null,
    });
  },

  /**
   * Registra múltiplas frequências em lote (RPC function)
   * @param {Array} frequencias - Array de objetos de frequência
   * @returns {Promise<Array>}
   */
  async registrarLote(frequencias) {
    const client = await getPostgrestClient();
    return client.rpc("registrar_frequencia_lote", {
      p_frequencias: frequencias,
    });
  },

  /**
   * Valida vínculo aluno-turma (RPC function)
   * @param {string} alunoId
   * @param {string} turmaId
   * @returns {Promise<boolean>}
   */
  async validarVinculo(alunoId, turmaId) {
    const client = await getPostgrestClient();
    const result = await client.rpc("validar_vinculo_aluno_turma", {
      p_aluno_id: alunoId,
      p_turma_id: turmaId,
    });
    return result === true;
  },

  /**
   * Busca frequência de uma turma para uma data
   * @param {string} turmaId
   * @param {string} data - Data no formato YYYY-MM-DD
   * @param {string} [atividadeId] - ID da atividade para filtrar (opcional)
   * @returns {Promise<Array>}
   */
  async getByTurma(turmaId, data, atividadeId = null) {
    const filters = {
      turma_id: `eq.${turmaId}`,
      data: `eq.${data}`,
    };

    if (atividadeId) {
      filters.atividade_id = `eq.${atividadeId}`;
    }

    const client = await getPostgrestClient();
    return client.get("frequencia", { filters });
  },

  /**
   * Busca frequência por atividade específica
   * @param {string} atividadeId - ID da atividade
   * @param {string} turmaId - ID da turma (opcional)
   * @param {string} data - Data no formato YYYY-MM-DD (opcional)
   * @returns {Promise<Array>}
   */
  async getByAtividade(atividadeId, turmaId = null, data = null) {
    const filters = {
      atividade_id: `eq.${atividadeId}`,
    };

    if (turmaId) {
      filters.turma_id = `eq.${turmaId}`;
    }

    if (data) {
      filters.data = `eq.${data}`;
    }

    const client = await getPostgrestClient();
    return client.get("frequencia", { filters });
  },

  /**
   * Busca frequência por período para histórico do aluno
   * Usa função RPC que permite acesso completo para monitores e gestores
   * @param {Object} periodoData
   * @param {string} periodoData.dataInicio
   * @param {string} periodoData.dataFim
   * @param {string} periodoData.turmaId
   * @param {string} periodoData.alunoId
   * @param {boolean} periodoData.useHistoricoFunction - Se true, usa função RPC que bypassa RLS
   * @returns {Promise<Array>}
   */
  async getByPeriodo(periodoData) {
    const { dataInicio, dataFim, turmaId, alunoId, useHistoricoFunction } = periodoData;



    // Se for histórico do aluno, usar função RPC que permite acesso completo
    if (useHistoricoFunction && alunoId) {

      try {
        // Construir parâmetros da RPC - passar sempre, mesmo que null
        // PostgREST pode ter problemas quando parâmetros são omitidos
        const rpcParams = {
          p_aluno_id: alunoId,
          p_data_inicio: dataInicio || null,
          p_data_fim: dataFim || null,
        };



        const client = await getPostgrestClient();
        let resultado;
        try {
          resultado = await client.rpc("buscar_frequencias_historico_aluno", rpcParams);
        } catch (error) {
          // Log detalhado do erro
          console.error("[frequencia.service] Erro na chamada RPC:", {
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            response: error.response,
            alunoId: alunoId,
            rpcParams: rpcParams
          });

          // Se falhar, tentar diferentes combinações de parâmetros
          console.warn("[frequencia.service] RPC falhou, tentando variações:", error.message);

          // Tentar sem datas primeiro
          try {
            const rpcParamsSemDatas = {
              p_aluno_id: alunoId,
            };
            resultado = await client.rpc("buscar_frequencias_historico_aluno", rpcParamsSemDatas);
          } catch (error2) {
            // Se ainda falhar, lançar o erro original para fazer fallback
            console.error("[frequencia.service] RPC falhou mesmo sem datas:", {
              message: error2.message,
              status: error2.status,
              statusText: error2.statusText,
              response: error2.response
            });
            throw error; // Lança o erro original para fazer fallback
          }
        }



        // Garantir que resultado é um array
        // PostgREST pode retornar um objeto único se houver apenas 1 resultado, ou um array
        const resultadoArray = Array.isArray(resultado)
          ? resultado
          : (resultado && typeof resultado === 'object' ? [resultado] : []);



        // Mapear resultado para formato esperado
        // A função RPC já retorna atividade_nome e atividade_tipo, então criamos o objeto atividade
        const frequenciasMapeadas = resultadoArray.map(freq => {
          // Criar objeto atividade se houver atividade_id
          const atividade = freq.atividade_id && (freq.atividade_nome || freq.atividade_tipo) ? {
            id: freq.atividade_id,
            nome: freq.atividade_nome || null,
            tipo: freq.atividade_tipo || null,
          } : null;

          return {
            frequencia_id: freq.frequencia_id,
            id: freq.frequencia_id,
            aluno_id: freq.aluno_id,
            alunoId: freq.aluno_id,
            turma_id: freq.turma_id,
            turmaId: freq.turma_id,
            data: freq.data,
            data_frequencia: freq.data,
            presente: freq.presente,
            observacoes: freq.observacoes,
            atividade_id: freq.atividade_id,
            atividadeId: freq.atividade_id,
            atividade_nome: freq.atividade_nome,
            atividadeNome: freq.atividade_nome,
            atividade_tipo: freq.atividade_tipo,
            atividadeTipo: freq.atividade_tipo,
            atividade: atividade,
            turma: {
              nome: freq.turma_nome,
              turma_id: freq.turma_id,
              id: freq.turma_id,
            },
            turmaNome: freq.turma_nome,
            registrado_em: freq.registrado_em,
            atualizado_em: freq.atualizado_em,
          };
        });



        return Array.isArray(frequenciasMapeadas) ? frequenciasMapeadas : [];
      } catch (error) {
        import("../lib/telemetria").then((m) => m.registrarFalha("frequencia.historico_rpc", error)).catch(() => {});
        // Se useHistoricoFunction é true mas a RPC falhou, fazer fallback para método normal
        // Isso permite que os dados sejam exibidos mesmo se o PostgREST não reconhecer a função
        if (useHistoricoFunction) {
          console.warn("[frequencia.service] RPC falhou, usando método normal como fallback");
          // Continuar para o método normal abaixo
        } else {
          // Se não for histórico, retornar array vazio em caso de erro
          return [];
        }
      }
    }

    // Se chegou aqui e useHistoricoFunction era true mas não tinha alunoId, retornar vazio
    // Mas se a RPC falhou, já fizemos fallback acima, então continuar normalmente
    if (useHistoricoFunction && !alunoId) {

      return [];
    }



    // PostgREST requer múltiplos filtros na mesma coluna como parâmetros separados
    // Construir query string manualmente para suportar data=gte.X&data=lte.Y
    const params = [];

    // Adiciona select primeiro (deve vir primeiro no PostgREST)
    // Inclui atividade_id para poder buscar informações da atividade depois
    params.push(`select=${encodeURIComponent("*,turma(nome,turma_id,sala_id)")}`);

    // Adiciona filtros de data como parâmetros separados
    if (dataInicio) {
      params.push(`data=gte.${encodeURIComponent(dataInicio)}`);
    }
    if (dataFim) {
      params.push(`data=lte.${encodeURIComponent(dataFim)}`);
    }

    // Adiciona outros filtros
    if (turmaId) {
      params.push(`turma_id=eq.${encodeURIComponent(turmaId)}`);
    }
    if (alunoId) {
      params.push(`aluno_id=eq.${encodeURIComponent(alunoId)}`);
    }

    const queryString = params.join("&");
    // Usar view 'frequencias' se disponível, senão usar tabela 'frequencia'
    const endpoint = "frequencias"; // Tenta usar view primeiro
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    const { postgrestClient: apiClient } = await import("../config/api");

    let frequencias = [];
    try {
      frequencias = await apiClient.request(url, { method: "GET" });
    } catch (error) {
      // Se a view não existir, tentar tabela direta
      console.warn("[frequencia.service] Erro ao buscar via view, tentando tabela direta:", error);
      const urlTabela = queryString ? `frequencia?${queryString}` : "frequencia";
      frequencias = await apiClient.request(urlTabela, { method: "GET" });
    }

    // Garantir que é um array
    frequencias = Array.isArray(frequencias) ? frequencias : [];

    // Se houver frequências com atividade_id, buscar informações das atividades
    if (Array.isArray(frequencias) && frequencias.length > 0) {
      const atividadesIds = [...new Set(frequencias.map(f => f.atividade_id).filter(Boolean))];

      if (atividadesIds.length > 0) {
        // Buscar informações das atividades esportivas e pedagógicas
        // Usar filtro in do PostgREST quando possível, ou buscar individualmente
        let atividadesEsportivas = [];
        let atividadesPedagogicas = [];

        // Obter cliente uma vez para usar em todo o bloco
        const client = await getPostgrestClient();

        try {
          // Tentar buscar todas de uma vez usando filtro in
          // PostgREST requer formato: id=in.(valor1,valor2,valor3)
          const atividadesEsportivasResult = await client.get("atividades_esportivas", {
            filters: { id: `in.(${atividadesIds.map(id => String(id).trim()).join(",")})` },
            select: "id,nome"
          }).catch((err) => {
            console.warn("[frequencia.service] Erro ao buscar atividades esportivas:", err);
            return [];
          });

          const atividadesPedagogicasResult = await client.get("atividades_pedagogicas", {
            filters: { id: `in.(${atividadesIds.map(id => String(id).trim()).join(",")})` },
            select: "id,nome"
          }).catch((err) => {
            console.warn("[frequencia.service] Erro ao buscar atividades pedagógicas:", err);
            return [];
          });

          atividadesEsportivas = Array.isArray(atividadesEsportivasResult) ? atividadesEsportivasResult : [];
          atividadesPedagogicas = Array.isArray(atividadesPedagogicasResult) ? atividadesPedagogicasResult : [];


        } catch (error) {
          console.error("[frequencia.service] Erro ao buscar atividades:", error);
          // Se falhar, buscar individualmente
          console.warn("Erro ao buscar atividades em lote, tentando individualmente:", error);
          const atividadesEsportivasPromises = atividadesIds.map(id =>
            client.get("atividades_esportivas", {
              filters: { id: `eq.${id}` },
              select: "id,nome"
            }).catch(() => null)
          );

          const atividadesPedagogicasPromises = atividadesIds.map(id =>
            client.get("atividades_pedagogicas", {
              filters: { id: `eq.${id}` },
              select: "id,nome"
            }).catch(() => null)
          );

          const [atividadesEsportivasResults, atividadesPedagogicasResults] = await Promise.all([
            Promise.all(atividadesEsportivasPromises),
            Promise.all(atividadesPedagogicasPromises)
          ]);

          // Flatten e filtrar resultados
          atividadesEsportivas = atividadesEsportivasResults
            .filter(r => r && Array.isArray(r) && r.length > 0)
            .flat();
          atividadesPedagogicas = atividadesPedagogicasResults
            .filter(r => r && Array.isArray(r) && r.length > 0)
            .flat();
        }

        // Criar mapa de atividades
        const atividadesMap = new Map();
        [...(Array.isArray(atividadesEsportivas) ? atividadesEsportivas : []),
        ...(Array.isArray(atividadesPedagogicas) ? atividadesPedagogicas : [])].forEach(ativ => {
          if (ativ && ativ.id) {
            // Verificar se é esportiva ou pedagógica
            const isEsportiva = Array.isArray(atividadesEsportivas) &&
              atividadesEsportivas.some(ae => ae && ae.id === ativ.id);
            atividadesMap.set(String(ativ.id), {
              id: ativ.id,
              nome: ativ.nome,
              tipo: isEsportiva ? "esportiva" : "pedagogica"
            });
          }
        });

        // Debug: log do mapa de atividades


        // Adicionar informações de atividades às frequências
        return frequencias.map(freq => {
          const atividadeId = freq.atividade_id ? String(freq.atividade_id) : null;
          const atividade = atividadeId ? atividadesMap.get(atividadeId) : null;

          // Debug: log para cada frequência
          if (atividadeId && !atividade) {
            console.warn(`[frequencia.service] Atividade ${atividadeId} não encontrada no mapa`);
          }

          return {
            ...freq,
            atividade: atividade,
            // Adicionar também diretamente no objeto para facilitar acesso
            atividade_nome: atividade?.nome || null,
            atividade_tipo: atividade?.tipo || null,
          };
        });
      }
    }

    return frequencias;
  },

};

export default frequenciaService;

